import type { Identifier, IdentifierKind, LinkRules, PhoneRules, RawIdentifier } from './types.ts'

/**
 * Identifier normalization — the substrate the whole layer stands on (ADR-0005 §2).
 *
 * It lives in core, not in the adapters, for one reason: two adapters that normalize a
 * phone differently produce two customers for one person, and no downstream rule can tell
 * that apart from two genuinely different people. One implementation, applied to every
 * source, is what makes `(kind, valueNormalized)` a usable identity key.
 *
 * Rejections are typed rather than dropped. "This card has no phone" and "this card's
 * phone is the switchboard extension 110" lead to different decisions upstream, and the
 * counts are the coverage report the pilot is measured on.
 */

/** Why a raw value did not become an identifier. */
export type IdentifierRejection =
  | 'empty'
  | 'placeholder'
  | 'malformed'
  | 'too_short'
  | 'manager_extension'
  | 'manager_contact'

export type NormalizationResult =
  | { ok: true; identifier: Identifier }
  | { ok: false; reason: IdentifierRejection }

/** Values sources write when they mean "nothing". None of them identify anybody. */
const PLACEHOLDERS = new Set(['-', '--', '—', 'n/a', 'na', 'none', 'null', 'нет', 'нема', '?'])

const isPlaceholderText = (s: string): boolean => PLACEHOLDERS.has(s.toLowerCase())

/** `0000000000`, `1111111`, … — a filled-in field that carries no information. */
const isRepeatedDigit = (digits: string): boolean => /^(\d)\1*$/.test(digits)

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Phone → `+<digits>`.
 *
 * No region is inferred unless the tenant configured one: guessing a country code merges
 * two people the moment the guess is wrong, and an unmerged pair is recoverable while a
 * wrongly merged one is not. Short numbers are extensions — they belong to managers
 * (`managers.phone_extensions`), never to a customer.
 */
export function normalizePhone(
  raw: string,
  rules: PhoneRules,
  extensions: readonly string[],
  managerPhones: readonly string[] = [],
): NormalizationResult {
  const trimmed = raw.trim()
  if (!trimmed) return { ok: false, reason: 'empty' }
  if (isPlaceholderText(trimmed)) return { ok: false, reason: 'placeholder' }

  let digits = trimmed.replace(/\D/g, '')
  if (!digits) return { ok: false, reason: 'placeholder' }
  if (isRepeatedDigit(digits)) return { ok: false, reason: 'placeholder' }

  // `00` is the international prefix in most of the world's dial plans; `+` is the same
  // thing typed differently, and the digit strip above already removed it.
  if (digits.startsWith('00')) digits = digits.slice(2)

  if (rules.defaultCallingCode && rules.stripTrunkPrefix && digits.startsWith('0')) {
    digits = `${rules.defaultCallingCode}${digits.slice(1)}`
  }

  if (extensions.includes(digits)) return { ok: false, reason: 'manager_extension' }
  if (managerPhones.includes(digits)) return { ok: false, reason: 'manager_contact' }
  if (digits.length < rules.minDigits) return { ok: false, reason: 'too_short' }

  return { ok: true, identifier: { kind: 'phone', valueNormalized: `+${digits}`, valueRaw: raw } }
}

/**
 * Email → trimmed, lowercased. Gmail's dot-and-plus folding is deliberately NOT applied:
 * it is correct for one provider and wrong for others, so it belongs in an explicit rule
 * rather than in the normalizer every source goes through.
 */
export function normalizeEmail(
  raw: string,
  managerEmails: readonly string[] = [],
): NormalizationResult {
  const value = raw.trim().toLowerCase()
  if (!value) return { ok: false, reason: 'empty' }
  if (isPlaceholderText(value)) return { ok: false, reason: 'placeholder' }
  if (!EMAIL_SHAPE.test(value)) return { ok: false, reason: 'malformed' }
  if (managerEmails.includes(value)) return { ok: false, reason: 'manager_contact' }
  return { ok: true, identifier: { kind: 'email', valueNormalized: value, valueRaw: raw } }
}

/**
 * The tenant's own people as the normaliser must see them: a manager's phone through the
 * same digit rules a card's phone takes, so the two meet; an email lowercased. Pure, so the
 * live seam and the audit's copy of `managers` derive the same lists.
 */
export function managerContactsOf(
  managers: readonly { phone: string | null; email: string | null }[],
  phone: PhoneRules,
): { managerPhones: string[]; managerEmails: string[] } {
  const phones = new Set<string>()
  const emails = new Set<string>()
  for (const manager of managers) {
    if (manager.phone !== null) {
      const result = normalizePhone(manager.phone, phone, [])
      if (result.ok) phones.add(result.identifier.valueNormalized.slice(1))
    }
    if (manager.email !== null) {
      const result = normalizeEmail(manager.email)
      if (result.ok) emails.add(result.identifier.valueNormalized)
    }
  }
  return { managerPhones: [...phones].sort(), managerEmails: [...emails].sort() }
}

/** Telegram → handle without `@`, lowercased. A `t.me/` link is the same handle. */
export function normalizeTelegram(raw: string): NormalizationResult {
  const value = raw
    .trim()
    .replace(/^(https?:\/\/)?(t\.me|telegram\.me)\//i, '')
    .replace(/^@/, '')
    .toLowerCase()
  if (!value) return { ok: false, reason: 'empty' }
  if (isPlaceholderText(value)) return { ok: false, reason: 'placeholder' }
  if (value.length < 3) return { ok: false, reason: 'too_short' }
  return { ok: true, identifier: { kind: 'telegram', valueNormalized: value, valueRaw: raw } }
}

/**
 * A source's own contact id, namespaced by the scope it is unique within. The namespace is
 * not decoration: two CRMs both numbering contacts from 1 would otherwise collapse into one
 * customer on the first pass, and that merge is the one mistake this layer cannot undo
 * cheaply.
 *
 * Case is preserved — external ids are opaque, and lowercasing them can collide.
 */
export function normalizeExternalRef(raw: string, namespace: string): NormalizationResult {
  const value = raw.trim()
  if (!value) return { ok: false, reason: 'empty' }
  if (isPlaceholderText(value)) return { ok: false, reason: 'placeholder' }
  if (/^\d+$/.test(value) && isRepeatedDigit(value)) return { ok: false, reason: 'placeholder' }
  return {
    ok: true,
    identifier: {
      kind: 'external_contact_ref',
      valueNormalized: `${namespace}:${value}`,
      valueRaw: raw,
    },
  }
}

/** One raw identifier through the rule set for its kind. */
export function normalizeIdentifier(raw: RawIdentifier, rules: LinkRules): NormalizationResult {
  switch (raw.kind) {
    case 'phone':
      return normalizePhone(raw.value, rules.phone, rules.managerExtensions, rules.managerPhones)
    case 'email':
      return normalizeEmail(raw.value, rules.managerEmails)
    case 'telegram':
      return normalizeTelegram(raw.value)
    case 'external_contact_ref':
      return normalizeExternalRef(raw.value, raw.source.namespace ?? raw.source.pipelineId)
  }
}

export interface NormalizedIdentifiers {
  /** Accepted, de-duplicated by `(kind, valueNormalized)`, in input order. */
  accepted: { raw: RawIdentifier; identifier: Identifier }[]
  /** Rejections by reason — the coverage report, not an error log. */
  rejected: Partial<Record<IdentifierRejection, number>>
}

/** Every identifier a card publishes, normalized once. */
export function normalizeCardIdentifiers(
  identifiers: readonly RawIdentifier[],
  rules: LinkRules,
): NormalizedIdentifiers {
  const accepted: NormalizedIdentifiers['accepted'] = []
  const rejected: NormalizedIdentifiers['rejected'] = {}
  const seen = new Set<string>()

  for (const raw of identifiers) {
    const result = normalizeIdentifier(raw, rules)
    if (!result.ok) {
      rejected[result.reason] = (rejected[result.reason] ?? 0) + 1
      continue
    }
    const key = identifierKey(result.identifier.kind, result.identifier.valueNormalized)
    if (seen.has(key)) continue
    seen.add(key)
    accepted.push({ raw, identifier: result.identifier })
  }

  return { accepted, rejected }
}

/** The identity key: what the unique index in `customer_identifiers` is built on. */
export const identifierKey = (kind: IdentifierKind, valueNormalized: string): string =>
  `${kind}:${valueNormalized}`

/**
 * Weight for an identifier by `<kind>:<pipelineKind>`, falling back to `<kind>`, then to a
 * neutral 0.5. Stored on the row so a later re-weighting is a visible change, not a silent
 * rewrite of what the last run believed.
 */
export function identifierConfidence(
  kind: IdentifierKind,
  pipelineKind: string,
  rules: LinkRules,
): number {
  return (
    rules.identifierConfidence[`${kind}:${pipelineKind}`] ?? rules.identifierConfidence[kind] ?? 0.5
  )
}
